if (!customElements.get('restock-eta')) {
  const API_URL = 'https://status.remattress.co/api/public/sheets-eta';
  const CACHE_KEY = 'repillow-restock-eta-v1';
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const DATE_PLACEHOLDER = '[[date]]';

  let loadPromise;

  function normalizeSku(sku) {
    return String(sku || '').trim().toUpperCase();
  }

  function stripFormatChars(value) {
    return String(value || '').replace(/[\u200B-\u200D\u2060\uFEFF]/g, '').trim();
  }

  function unquote(value) {
    const text = String(value || '').trim();
    if (text.length >= 2 && text.startsWith('"') && text.endsWith('"')) {
      return text.slice(1, -1).replace(/""/g, '"');
    }
    return text;
  }

  function parseCsv(text) {
    const map = {};
    const rows = String(text || '').replace(/^\uFEFF/, '').split(/\r?\n/);

    for (let i = 0; i < rows.length; i += 1) {
      const line = rows[i].trim();
      if (!line) continue;

      const match = line.match(/^("(?:[^"]|"")*"|[^,]*?)\s*,\s*("(?:[^"]|"")*"|.*)$/);
      if (!match) continue;

      const sku = normalizeSku(unquote(match[1]));
      const eta = stripFormatChars(unquote(match[2]));
      if (!sku || sku === 'SKU') continue;
      if (!eta || eta.toLowerCase() === 'eta') continue;
      map[sku] = eta;
    }

    return map;
  }

  function readSessionCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (!cached || !cached.map || typeof cached.map !== 'object') return null;
      if (Date.now() - cached.ts > CACHE_TTL_MS) return null;
      return cached.map;
    } catch (error) {
      return null;
    }
  }

  function writeSessionCache(map) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), map }));
    } catch (error) {
      // Private mode or full storage — in-memory cache still works.
    }
  }

  function loadEtaMap() {
    if (loadPromise) return loadPromise;

    const cached = readSessionCache();
    if (cached) {
      loadPromise = Promise.resolve(cached);
      return loadPromise;
    }

    loadPromise = fetch(API_URL, { credentials: 'omit' })
      .then((response) => {
        if (!response.ok) throw new Error('restock eta http ' + response.status);
        return response.text();
      })
      .then((text) => {
        const map = parseCsv(text);
        writeSessionCache(map);
        return map;
      })
      .catch(() => {
        loadPromise = null;
        return null;
      });

    return loadPromise;
  }

  function hongKongTodayParts() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Hong_Kong',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());
    const read = (type) => Number(parts.find((part) => part.type === type)?.value);

    return {
      year: read('year'),
      month: read('month'),
      day: read('day')
    };
  }

  function parseSheetEta(raw) {
    const eta = stripFormatChars(raw);
    if (!eta) return null;
    if (eta === '有') return { type: 'in_production' };

    const match = eta.match(/^(\d{1,2})\s*\/\s*(\d{1,2})$/);
    if (!match) return null;

    const day = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const today = hongKongTodayParts();
    let year = today.year;
    if (month < today.month || (month === today.month && day < today.day)) {
      year += 1;
    }

    return { type: 'date', year, month, day };
  }

  function formatEtaDate(parsed, lang) {
    if (String(lang || '').toLowerCase().startsWith('zh')) {
      return parsed.month + '月' + parsed.day + '日';
    }

    const date = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day));
    return new Intl.DateTimeFormat(lang || 'en', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'short'
    }).format(date);
  }

  customElements.define(
    'restock-eta',
    class RestockEta extends HTMLElement {
      connectedCallback() {
        this._connected = true;
        this.render();
      }

      disconnectedCallback() {
        this._connected = false;
      }

      render() {
        const sku = normalizeSku(this.getAttribute('data-sku'));
        if (!sku) return;

        loadEtaMap().then((map) => {
          if (!this._connected || !map) return;

          const parsed = parseSheetEta(map[sku]);
          if (!parsed) return;

          if (parsed.type === 'in_production') {
            const copy = this.getAttribute('data-in-production');
            if (copy) this.textContent = copy;
            return;
          }

          const template = this.getAttribute('data-eta-template') || DATE_PLACEHOLDER;
          const formatted = formatEtaDate(parsed, document.documentElement.lang);
          this.textContent = template.replace(DATE_PLACEHOLDER, formatted);
        });
      }
    }
  );
}
